import { Component, computed, inject, input, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { TabComponent } from '../../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../../components/tab-layout/tab-layout.component';
import { WiperLoadingComponent } from '../../../../components/wiper-loading/wiper-loading.component';
import { CalendarComponent } from '../../../../components/calendar/calendar.component';
import { environment } from '../../../../../environments/environment';
import { ConfigService } from '../../../../services/config.service';

interface PlanDateStatus {
  date: string;
  hasPlan: boolean;
}

interface PlannedActivity {
  date: string;
  eventType: string;
  Customer?: string;
  NFA?: string;
  Status: string;
}

interface DatesStatusResponse {
  success: boolean;
  data: {
    plans: PlanDateStatus[];
  };
}

interface PlannedActivitiesResponse {
  success: boolean;
  data: {
    analytics: PlannedActivity[];
  };
}

type ActivityViewTab = 'visits' | 'nfa';

@Component({
  selector: 'app-planner-tab',
  imports: [CommonModule, TabLayoutComponent, WiperLoadingComponent, CalendarComponent],
  providers: [
    { provide: TabComponent, useExisting: PlannerTabComponent },
  ],
  templateUrl: './planner-tab.component.html',
  styleUrl: './planner-tab.component.scss',
})
export class PlannerTabComponent extends TabComponent {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);

  override name = this.dictionaryService.get('Planner');

  userId = input.required<string>();

  calendarMarkedDates = signal<Set<string>>(new Set());

  activities = signal<PlannedActivity[]>([]);
  activitiesLoading = signal(false);
  selectedDate = signal<string | null>(null);
  calendarLoading = signal(false);
  activeViewTab = signal<ActivityViewTab>('visits');

  singleVisits = computed(() =>
    this.activities().filter((a) => a.eventType === 'SingleVisit'),
  );
  nfaActivities = computed(() =>
    this.activities().filter((a) => a.eventType === 'NFA'),
  );
  hasVisits = computed(() => this.singleVisits().length > 0);
  hasNfas = computed(() => this.nfaActivities().length > 0);
  hasBothTypes = computed(() => this.hasVisits() && this.hasNfas());
  displayedActivities = computed(() =>
    this.activeViewTab() === 'visits'
      ? this.singleVisits()
      : this.nfaActivities(),
  );

  public async main(): Promise<void> {
    await this.loadMonthData();
  }

  async onMonthChanged(event: { year: number; month: number }): Promise<void> {
    this.selectedDate.set(null);
    this.activities.set([]);
    this.calendarMarkedDates.set(new Set());
    await this.loadMonthData(event.year, event.month);
  }

  onDaySelected(dateStr: string): void {
    this.selectedDate.set(dateStr);
    this.activeViewTab.set('visits');
    this.loadActivities(dateStr);
  }

  switchViewTab(tab: ActivityViewTab): void {
    this.activeViewTab.set(tab);
  }

  private async loadMonthData(year?: number, month?: number): Promise<void> {
    this.calendarLoading.set(true);
    const date = (year !== undefined && month !== undefined)
      ? new Date(year, month, 1)
      : new Date();
    const y = date.getFullYear();
    const m = date.getMonth();

    const from = this.configService.formatDateTimeForApi(new Date(y, m, 1));
    const lastDay = new Date(y, m + 1, 0).getDate();
    const to = this.configService.formatDateTimeForApi(new Date(y, m, lastDay), true);

    try {
      const response = await lastValueFrom(
        this.http.get<DatesStatusResponse>(
          `${environment.apiBaseUrl}/plans/analytics/users/${this.userId()}/dates-status?from=${from}&to=${to}`,
        ),
      );
      if (response.success) {
        const marked = new Set<string>();
        for (const plan of response.data.plans) {
          if (plan.hasPlan) marked.add(plan.date);
        }
        this.calendarMarkedDates.set(marked);
      }
    } catch {
      // proceed with empty plan data
    } finally {
      this.calendarLoading.set(false);
    }
  }

  private loadActivities(date: string): void {
    this.activitiesLoading.set(true);

    this.http
      .get<PlannedActivitiesResponse>(
        `${environment.apiBaseUrl}/plans/analytics/planned-activities/${this.userId()}?date=${date}`,
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.activities.set(response.data.analytics);
            const hasVisits = response.data.analytics.some(
              (a) => a.eventType === 'SingleVisit',
            );
            const hasNfas = response.data.analytics.some(
              (a) => a.eventType === 'NFA',
            );
            if (!hasVisits && hasNfas) {
              this.activeViewTab.set('nfa');
            } else {
              this.activeViewTab.set('visits');
            }
          }
          this.activitiesLoading.set(false);
        },
        error: () => {
          this.activities.set([]);
          this.activitiesLoading.set(false);
        },
      });
  }

  getActivityStatusClass(status: string): string {
    switch (status) {
      case 'PlannedVisit':
        return 'status-planned';
      case 'UnplannedVisit':
        return 'status-unplanned';
      case 'Unvisited':
        return 'status-unvisited';
      case 'PlannedNFA':
        return 'status-planned';
      case 'UnplannedNFA':
        return 'status-unplanned';
      case 'UnperformedNFA':
        return 'status-unvisited';
      default:
        return 'status-unvisited';
    }
  }

  getActivityStatusLabel(status: string): string {
    switch (status) {
      case 'PlannedVisit':
        return this.dictionaryService.get('PlannedVisit') || 'Planned Visit';
      case 'UnplannedVisit':
        return this.dictionaryService.get('UnplannedVisit') || 'Unplanned Visit';
      case 'Unvisited':
        return this.dictionaryService.get('Unvisited') || 'Unvisited';
      case 'PlannedNFA':
        return this.dictionaryService.get('PlannedNFA') || 'Planned NFA';
      case 'UnplannedNFA':
        return this.dictionaryService.get('UnplannedNFA') || 'Unplanned NFA';
      case 'UnperformedNFA':
        return this.dictionaryService.get('UnperformedNFA') || 'Unperformed NFA';
      default:
        return status;
    }
  }
}
